import {
  arrayRemove,
  arrayUnion,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore"
import {
  getDb,
  jobDoc,
  jobsCollection,
  pickDoc,
  picksCollection,
  sessionDoc,
  toCardPick,
  toJob,
} from "@/lib/firebase/firestore"
import { EMPTY_DEEP_DIVE } from "@/types"
import { byCreatedAt, pendingAsNow } from "@/utils/timestamps"
import { stripUndefined } from "@/utils/firestoreWrite"
import type { CardId, CardPick, DeepDiveDetail, Job, JobInput, MoscowBucket } from "@/types"

/*
 * Jobs: the problem statement, the cards chosen for it, where they were filed,
 * and the deep dive.
 *
 * WHO MAY CALL WHAT — enforced by the Firestore rules as well as by the UI.
 *
 *   Facilitators only: startJob, updateJob, setJobCards, seedJobShortlist,
 *                      placeCard, setCardNote, clearPlacement, setDeepDiveCard,
 *                      setDeepDive, completeJob, closeCurrentJob
 *   Anyone in the session: listJobs, listPicks, addMyPick / removeMyPick /
 *                      clearMyPicks (their own document)
 *
 * The asymmetry is the flow itself: the room picks cards, the facilitator
 * decides what the job keeps.
 */

/**
 * Opens a job for a set of pain points and points the session at it, in one
 * transaction.
 *
 * Both writes have to land together. Written separately, a failure between them
 * leaves either a job nobody is pointed at — invisible, and its picks
 * unreachable — or a session pointing at a job that does not exist, which
 * strands every client on a screen with no data and no error.
 *
 * Idempotent per pain set: if the session is already working exactly these
 * pains, the existing job id comes back instead of a second one being created.
 * Starting a job is a single button, and a double-click would otherwise produce
 * two jobs with one of them orphaned the moment the pointer moved.
 */
export async function startJob(
  sessionId: string,
  painIds: string[],
  statement: JobInput = {}
): Promise<string> {
  // Pre-generated so the transaction can create the document directly:
  // transactions cannot use `addDoc`.
  const jobRef = doc(jobsCollection(sessionId))
  const wanted = [...painIds].sort().join("|")

  return runTransaction(getDb(), async (tx) => {
    const sessionRef = sessionDoc(sessionId)
    const session = await tx.get(sessionRef)
    if (!session.exists()) throw new Error(`Session ${sessionId} does not exist`)

    const currentJobId = session.get("currentJobId") as string | null
    if (currentJobId) {
      const current = await tx.get(jobDoc(sessionId, currentJobId))
      const job = toJob(current)
      if (job && [...job.painIds].sort().join("|") === wanted) return currentJobId
    }

    tx.set(jobRef, {
      painIds,
      who: statement.who ?? "",
      what: statement.what ?? "",
      why: statement.why ?? "",
      cardIds: [],
      shortlistSeeded: false,
      placements: {},
      deepDive: {},
      deepDiveCardId: null,
      createdAt: serverTimestamp(),
      completedAt: null,
    })
    tx.update(sessionRef, { currentJobId: jobRef.id, updatedAt: serverTimestamp() })

    return jobRef.id
  })
}

/** Oldest first: array position IS the job's number in the session. */
export async function listJobs(sessionId: string): Promise<Job[]> {
  const snapshot = await getDocs(jobsCollection(sessionId))
  return snapshot.docs
    .map(toJob)
    .filter((j): j is Job => j !== null)
    .sort(byCreatedAt)
}

/** The statement itself: "As a <who>, I want <what>, so that <why>." */
export async function updateJob(sessionId: string, jobId: string, input: JobInput): Promise<void> {
  await updateDoc(jobDoc(sessionId, jobId), stripUndefined(input))
}

/* ------------------------------------------------------------- card picking */

/**
 * Adds one card to a person's hand, or takes one back out.
 *
 * ONE CARD AT A TIME, as a server-side array transform — never the whole array.
 *
 * A hand is a Firestore document, so the only version of it the picker's screen
 * can read is the last snapshot the listener delivered. Writing `[...mine, card]`
 * is therefore a read-modify-write against state one round trip behind, and
 * dealing quickly is precisely the case where that round trip has not finished:
 * the second card is written on top of a hand that has never heard of the first,
 * and the first is silently gone. `arrayUnion`/`arrayRemove` are applied by the
 * server against whatever is actually stored, so two decisions in flight at the
 * same instant both land — the same reason a vote is an `arrayUnion` on a pain.
 *
 * `setDoc` with `merge` rather than `updateDoc`, because the first card someone
 * keeps has no document to update yet; merge creates it. The rules allow only
 * `cardIds` and `updatedAt`, and both are written here, so the merged result
 * still satisfies them.
 *
 * `arrayUnion` de-duplicates, so a double-tap cannot put a card in a hand twice.
 *
 * `handCap` is applied by the picker's screen. Enforcing it here would mean
 * reading the session config on every tap to stop something no interface offers.
 */
export async function addMyPick(
  sessionId: string,
  jobId: string,
  participantId: string,
  cardId: CardId
): Promise<void> {
  await setDoc(
    pickDoc(sessionId, jobId, participantId),
    { cardIds: arrayUnion(cardId), updatedAt: serverTimestamp() },
    { merge: true }
  )
}

export async function removeMyPick(
  sessionId: string,
  jobId: string,
  participantId: string,
  cardId: CardId
): Promise<void> {
  await setDoc(
    pickDoc(sessionId, jobId, participantId),
    { cardIds: arrayRemove(cardId), updatedAt: serverTimestamp() },
    { merge: true }
  )
}

export async function listPicks(sessionId: string, jobId: string): Promise<CardPick[]> {
  const snapshot = await getDocs(picksCollection(sessionId, jobId))
  return snapshot.docs
    .map(toCardPick)
    .filter((p): p is CardPick => p !== null)
    .sort(
      (a, b) => pendingAsNow(a.updatedAt) - pendingAsNow(b.updatedAt) || a.id.localeCompare(b.id)
    )
}

export async function clearMyPicks(
  sessionId: string,
  jobId: string,
  participantId: string
): Promise<void> {
  await deleteDoc(pickDoc(sessionId, jobId, participantId))
}

/**
 * The facilitator's finalised shortlist, in the order they arranged it.
 *
 * Kept apart from the picks it usually comes from, because the facilitator may
 * drop a card several people chose and add one nobody did — so a tally of picks
 * can never stand in for this, and overwriting the picks to match would destroy
 * the tally they used to decide.
 *
 * Placements and deep-dive answers for cards no longer on the list are cleared
 * in the same write. Left behind they would be invisible but real, and a card
 * brought back later would silently reappear already filed under a bucket the
 * room does not remember agreeing to.
 */
export async function setJobCards(
  sessionId: string,
  jobId: string,
  cardIds: CardId[]
): Promise<void> {
  await runTransaction(getDb(), async (tx) => {
    const ref = jobDoc(sessionId, jobId)
    const snapshot = await tx.get(ref)
    if (!snapshot.exists()) throw new Error(`Job ${jobId} does not exist`)

    const job = toJob(snapshot)!
    const keep = new Set(cardIds)
    const patch: Record<string, unknown> = { cardIds }

    for (const cardId of Object.keys(job.placements)) {
      if (!keep.has(cardId)) patch[`placements.${cardId}`] = deleteField()
    }
    for (const cardId of Object.keys(job.deepDive)) {
      if (!keep.has(cardId)) patch[`deepDive.${cardId}`] = deleteField()
    }

    tx.update(ref, patch)
  })
}

/**
 * Pre-fills the shortlist with the room's most-nominated cards, once.
 *
 * The facilitator arrives at the shortlist reading a list and taking things off
 * it, not building one from nothing — that is the difference between a minute
 * and five in a live room, and it puts the room's own answer on screen as the
 * starting position rather than an empty column.
 *
 * Guarded by `shortlistSeeded` inside the transaction rather than by "is
 * `cardIds` empty", because those two are not the same question: a facilitator
 * who drops every card has an empty shortlist on purpose, and stepping back to
 * the picks and forward again must not undo that.
 */
export async function seedJobShortlist(
  sessionId: string,
  jobId: string,
  cardIds: CardId[]
): Promise<void> {
  await runTransaction(getDb(), async (tx) => {
    const ref = jobDoc(sessionId, jobId)
    const snapshot = await tx.get(ref)
    if (!snapshot.exists()) throw new Error(`Job ${jobId} does not exist`)
    if (snapshot.get("shortlistSeeded") === true) return

    // The flag lands even when there is nothing to seed, so a job whose room
    // picked nothing is not re-examined every time the facilitator steps back.
    tx.update(ref, { shortlistSeeded: true, ...(cardIds.length > 0 ? { cardIds } : {}) })
  })
}

/* -------------------------------------------------------------- priorities */

/**
 * Files one card under a MoSCoW bucket, live, while the room watches.
 *
 * Dot-notation paths, because `updateDoc` replaces a nested map wholesale:
 * writing `{ placements: { [cardId]: … } }` would delete every other card's
 * placement. The note is written on its own path too, so moving a card between
 * buckets cannot wipe what was said about it.
 *
 * `mustHaveCap` is the facilitator's own limit and is applied by their screen.
 * A rule here would have to count the map on every drag.
 */
export async function placeCard(
  sessionId: string,
  jobId: string,
  cardId: CardId,
  bucket: MoscowBucket
): Promise<void> {
  await updateDoc(jobDoc(sessionId, jobId), { [`placements.${cardId}.bucket`]: bucket })
}

export async function setCardNote(
  sessionId: string,
  jobId: string,
  cardId: CardId,
  note: string
): Promise<void> {
  await updateDoc(jobDoc(sessionId, jobId), { [`placements.${cardId}.note`]: note })
}

/** Takes a card back off the board without dropping it from the shortlist. */
export async function clearPlacement(
  sessionId: string,
  jobId: string,
  cardId: CardId
): Promise<void> {
  await updateDoc(jobDoc(sessionId, jobId), { [`placements.${cardId}`]: deleteField() })
}

/* -------------------------------------------------------------- deep dive */

/**
 * Brings one card up for the whole room in the deep dive.
 *
 * The card the dive is on is shared state, not each client's own: the round is
 * run out loud, so a facilitator saying "next one" has to move every screen. A
 * plain `updateDoc` is enough — there is one writer, and the listeners do the
 * rest.
 */
export async function setDeepDiveCard(
  sessionId: string,
  jobId: string,
  cardId: CardId
): Promise<void> {
  await updateDoc(jobDoc(sessionId, jobId), { deepDiveCardId: cardId })
}

/**
 * One box of the deep dive, per card, per field.
 *
 * A path per field rather than the whole detail: the four boxes are filled in
 * over several minutes of conversation, and writing the object each time would
 * mean every save carrying a stale copy of the other three.
 *
 * The other three are seeded on first write, because a dot path creates only
 * what it names — a card with just `metric` set would otherwise read back
 * missing the fields the mapper expects, and the form would render undefined.
 */
export async function setDeepDive(
  sessionId: string,
  jobId: string,
  cardId: CardId,
  patch: Partial<DeepDiveDetail>
): Promise<void> {
  await runTransaction(getDb(), async (tx) => {
    const ref = jobDoc(sessionId, jobId)
    const snapshot = await tx.get(ref)
    if (!snapshot.exists()) throw new Error(`Job ${jobId} does not exist`)

    const existing = toJob(snapshot)!.deepDive[cardId]
    if (!existing) {
      tx.update(ref, { [`deepDive.${cardId}`]: { ...EMPTY_DEEP_DIVE, ...stripUndefined(patch) } })
      return
    }

    const payload: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(stripUndefined(patch))) {
      payload[`deepDive.${cardId}.${key}`] = value
    }
    if (Object.keys(payload).length > 0) tx.update(ref, payload)
  })
}

/* ------------------------------------------------------------------ closing */

/**
 * Marks a job finished and clears the session's pointer, in one transaction.
 *
 * The job document is kept, not deleted: its cards, placements and deep dive are
 * the session's result, and the report reads them back. Only the pointer moves,
 * so the facilitator can open a second job for another pain without the first
 * one's work following it.
 *
 * The pointer is checked before it is cleared, so a close arriving late — after
 * the facilitator has already opened the next job — cannot clear a pointer that
 * has moved on.
 */
export async function completeJob(sessionId: string, jobId: string): Promise<void> {
  await runTransaction(getDb(), async (tx) => {
    const sessionRef = sessionDoc(sessionId)
    const snapshot = await tx.get(sessionRef)
    if (!snapshot.exists()) throw new Error(`Session ${sessionId} does not exist`)

    tx.update(jobDoc(sessionId, jobId), { completedAt: serverTimestamp() })
    if (snapshot.get("currentJobId") === jobId) {
      tx.update(sessionRef, { currentJobId: null, updatedAt: serverTimestamp() })
    }
  })
}

/**
 * Clears the pointer without finishing the job — for backing out of one opened
 * by mistake, where "completed" would be a lie the report then repeats.
 */
export async function closeCurrentJob(sessionId: string, jobId: string): Promise<void> {
  await runTransaction(getDb(), async (tx) => {
    const ref = sessionDoc(sessionId)
    const snapshot = await tx.get(ref)
    if (!snapshot.exists()) throw new Error(`Session ${sessionId} does not exist`)
    if (snapshot.get("currentJobId") !== jobId) return
    tx.update(ref, { currentJobId: null, updatedAt: serverTimestamp() })
  })
}
